import React, {useEffect, useRef, useState} from 'react';
import {appRouter} from '@netx/core-next/instance/router';
import clsx from 'clsx';
import {withQueryClient} from '@netx/core-next/lib';
import {
	composeClasses,
	styled,
	WithClasses,
	withTheme,
} from '@netx/core-components/Theme';
import {TabsProvider} from '@netx/core-components/Tabs';
import {TabPanel} from '@netx/core-components/TabPanel';
import {
	unauthenticatedLandingClasses,
	getUnauthenticatedLandingUtilityClass,
	UnauthenticatedLandingClasses,
	UNAUTHENTICATED_LANDING_CLASS_NAME,
} from './unauthenticatedLandingClasses';
import {Login} from '../Login';
import {Register} from '../Register';
import {ResetPassword} from '../ResetPassword';
import {ResetPasswordRequest} from '../ResetPasswordRequest';
import useDispatcher from '@netx/core-components/hooks/useDispatcher';
import useEvent from '@netx/core-components/hooks/useEvent';
import {getLoginBackgroundUrl} from '@netx/core/css';

export type UnauthenticatedLandingOwnerState = WithClasses<
	UnauthenticatedLandingClasses,
	{}
>;

const useUtilityClasses = (ownerState: UnauthenticatedLandingOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
		content: ['content'],
		tabPanel: ['tabPanel'],
	};
	return composeClasses(slots, getUnauthenticatedLandingUtilityClass, classes);
};

const UnauthenticatedLandingRoot = styled('div', {
	name: UNAUTHENTICATED_LANDING_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	...theme.mixins.absoluteFill,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: `var(--mdc-theme-background)`,
	// DAM-20791: there is a bug in chromium that causes background images, set via css variables,
	// to do a fresh reload on clicks/style changes (the only other workaraound atm is to changes
	// the cache policy on the sys ops side)
	// background-image: var(--mdc-theme-login-background-image);
	backgroundPosition: `var(--mdc-theme-login-background-position)`,
	backgroundRepeat: `var(--mdc-theme-login-background-repeat)`,
	backgroundSize: 'cover',
}));

const UnauthenticatedLandingContent = styled('div', {
	name: UNAUTHENTICATED_LANDING_CLASS_NAME,
	slot: 'Content',
})(({theme}) => ({
	borderRadius: theme.spacing(2),
	boxShadow: theme.shadows[2],
	maxHeight: `calc(100% - 64px)`,
	width: 500,
	maxWidth: '96%',
	// padding: theme.spacing(4, 0), //6),
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
	overflow: 'hidden',
	'&::before': {
		...theme.mixins.absoluteFill,
		content: '""',
		borderRadius: theme.spacing(2),
		backgroundColor: theme.palette.background.paper,
		opacity: 0.98,
	},
}));

const UnauthenticatedLandingTabPanel = styled(TabPanel, {
	name: UNAUTHENTICATED_LANDING_CLASS_NAME,
	slot: 'TabPanel',
})(({theme}) => ({
	zIndex: 2,
	'& > *': {
		width: '100%',
	},
}));

export type UnauthenticatedLandingProps = WithClasses<
	UnauthenticatedLandingClasses,
	{}
>;

export const UnauthenticatedLandingPrimitive = ({
	classes: _classes,
	className,
	...props
}: UnauthenticatedLandingProps) => {
	const ref = useRef<HTMLDivElement>(null);
	const [tabIndex, setTabIndex] = useState(0);
	const [passwordResetParams, setPasswordResetParams] = useState({
		login: '',
		token: '',
	});

	const ownerState: UnauthenticatedLandingOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	useEffect(() => {
		const loginBackgroundUrl = getLoginBackgroundUrl();
		if (!loginBackgroundUrl || !ref.current) return;
		ref.current.style.backgroundImage = `url(${loginBackgroundUrl})`;
	}, []);

	useDispatcher('auth:user:login:show', () => {
		setTabIndex(0);
	});

	useDispatcher('auth:user:password:request', () => {
		setTabIndex(2);
	});

	useDispatcher('auth:user:register', () => {
		setTabIndex(1);
	});

	useDispatcher('auth:user:password:change', (login: string, token: string) => {
		setPasswordResetParams({
			login,
			token, // Buffer.from(token, 'base64').toString().split('~')[0],
		});
		setTabIndex(3);
	});

	const returnToLogin = useEvent(() => {
		appRouter.navigate('login', true);
	});

	return (
		<UnauthenticatedLandingRoot
			className={clsx(classes.root, className)}
			ref={ref}
		>
			<UnauthenticatedLandingContent className={classes.content}>
				<TabsProvider index={tabIndex}>
					{({activeTabIndex}) => (
						<>
							<UnauthenticatedLandingTabPanel
								aria-labelledby={undefined}
								id={`login-tab-panel`}
								className={classes.tabPanel}
								disablePadding
								index={0}
								value={activeTabIndex}
							>
								<Login
									onRequestPasswordReset={() => {
										appRouter.navigate('password-reset', true);
									}}
									onRequestRegistration={() => {
										appRouter.navigate('register', true);
									}}
								/>
							</UnauthenticatedLandingTabPanel>
							<UnauthenticatedLandingTabPanel
								aria-labelledby={undefined}
								id="register-tab-panel"
								className={classes.tabPanel}
								disablePadding
								index={1}
								value={activeTabIndex}
							>
								<Register onCancel={returnToLogin} />
							</UnauthenticatedLandingTabPanel>
							<UnauthenticatedLandingTabPanel
								aria-labelledby={undefined}
								id="reset-password-request-tab-panel"
								className={classes.tabPanel}
								disablePadding
								index={2}
								value={activeTabIndex}
							>
								<ResetPasswordRequest onCancel={returnToLogin} />
							</UnauthenticatedLandingTabPanel>
							<UnauthenticatedLandingTabPanel
								aria-labelledby={undefined}
								id="reset-password-tab-panel"
								className={classes.tabPanel}
								disablePadding
								index={3}
								value={activeTabIndex}
							>
								<ResetPassword
									onCancel={returnToLogin}
									{...passwordResetParams}
								/>
							</UnauthenticatedLandingTabPanel>
						</>
					)}
				</TabsProvider>
			</UnauthenticatedLandingContent>
		</UnauthenticatedLandingRoot>
	);
};

export const UnauthenticatedLanding = withTheme(
	withQueryClient(UnauthenticatedLandingPrimitive),
);

export default UnauthenticatedLanding;
